import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, Spinner } from './shared';
import Modal from './shared/Modal';
import InsightStatsPanel from './InsightStatsPanel';
import RichTextRenderer from '../RichTextRenderer';

function RenderedContent({ contentType, content, structured }) {
    if (!content) {
        if (structured) {
            return (
                <pre className="text-xs font-mono text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto">
                    {JSON.stringify(structured, null, 2)}
                </pre>
            );
        }
        return <span className="text-gray-400">(empty)</span>;
    }
    if (contentType === 'json') {
        let pretty = content;
        try { pretty = JSON.stringify(JSON.parse(content), null, 2); } catch { /* keep raw */ }
        return (
            <pre className="text-xs font-mono text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto">
                {pretty}
            </pre>
        );
    }
    if (contentType === 'html') {
        return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: content }} />;
    }
    if (contentType === 'text') {
        return <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">{content}</pre>;
    }
    return <RichTextRenderer text={content} />;
}

const CATEGORY_LABEL = {
    analysis_report: 'Analysis Report',
    technical_snapshot: 'Technical Snapshot',
    event_brief: 'Event Brief',
    ops_playbook: 'Ops Playbook',
};

function SinkCard({ sink }) {
    const title = sink.sink_name?.toUpperCase() || 'SINK';
    const accent =
        sink.sink_name === 's3' ? 'bg-sky-50 border-sky-200'
        : sink.sink_name === 'rag' ? 'bg-emerald-50 border-emerald-200'
        : sink.sink_name === 'db' ? 'bg-gray-50 border-gray-200'
        : 'bg-amber-50 border-amber-200';

    const renderBody = () => {
        if (sink.error) {
            return <Alert variant="error" className="mt-2">{sink.error}</Alert>;
        }
        if (sink.kind === 's3') {
            return (
                <div className="space-y-1.5 text-xs">
                    <div>
                        <span className="text-gray-500">Bucket:</span>{' '}
                        <span className="font-mono">{sink.bucket}</span>
                    </div>
                    <div className="break-all">
                        <span className="text-gray-500">Key:</span>{' '}
                        <span className="font-mono">{sink.key}</span>
                    </div>
                    {sink.download_url && (
                        <div className="pt-1">
                            <a
                                href={sink.download_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-sky-600 text-white text-xs font-medium hover:bg-sky-700"
                            >
                                Open S3 object ↗
                                <span className="opacity-70">({sink.expires_in}s)</span>
                            </a>
                        </div>
                    )}
                </div>
            );
        }
        if (sink.kind === 'rag') {
            return (
                <div className="space-y-1.5 text-xs">
                    <div>
                        <span className="text-gray-500">Collection:</span>{' '}
                        <span className="font-mono">{sink.collection}</span>
                    </div>
                    <div className="break-all">
                        <span className="text-gray-500">Document:</span>{' '}
                        <span className="font-mono">{sink.document_id}</span>
                    </div>
                    {sink.source_url && (
                        <div className="break-all">
                            <span className="text-gray-500">Source URL:</span>{' '}
                            <span className="font-mono">{sink.source_url}</span>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        <a
                            href={sink.admin_collection_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
                        >
                            Browse collection ↗
                        </a>
                        <a
                            href={sink.admin_search_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-600 text-emerald-700 text-xs font-medium hover:bg-emerald-50"
                        >
                            Search this doc ↗
                        </a>
                    </div>
                </div>
            );
        }
        return (
            <pre className="text-xs font-mono bg-white/60 p-2 rounded border border-gray-200 overflow-x-auto">
                {JSON.stringify(sink, null, 2)}
            </pre>
        );
    };

    return (
        <div className={`rounded-lg border p-4 ${accent}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">{title}</span>
                    <Badge variant="outline">{sink.kind}</Badge>
                </div>
                {sink.checksum && (
                    <span className="text-[10px] font-mono text-gray-400">sha {sink.checksum}</span>
                )}
            </div>
            {renderBody()}
        </div>
    );
}

const TAB_NAMES = ['content', 'structured', 'attributes', 'source_meta', 'sinks', 'stats'];

const FLASH_TIMEOUT_MS = 4000;
const STATS_QUERY = { topViewersLimit: 20, dailyWindowDays: 30 };

const TAB_LABELS = {
    content: 'Content',
    structured: 'Structured',
    attributes: 'Attributes',
    source_meta: 'Source meta',
    sinks: 'Sinks',
    stats: 'Stats',
};

const STATUS_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'content_ready', label: 'Content ready' },
    { value: 'published', label: 'Published — hiển thị trên feed' },
    { value: 'unpublished', label: 'Unpublished — gỡ khỏi feed' },
    { value: 'rejected', label: 'Rejected' },
];

const STATUS_TONE = {
    new: 'bg-gray-100 text-gray-700 border-gray-200',
    content_ready: 'bg-amber-50 text-amber-700 border-amber-200',
    published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    unpublished: 'bg-gray-100 text-gray-600 border-gray-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
};

const Icon = ({ d }) => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
);

const ICONS = {
    status: <Icon d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />,
    external: <Icon d="M14 5l7 7m0 0l-7 7m7-7H3" />,
    transcript: <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
    refresh: <Icon d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />,
    reindex: <Icon d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />,
    trash: <Icon d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />,
};

function ActionMenu({ items }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const escHandler = (e) => e.key === 'Escape' && setOpen(false);
        document.addEventListener('mousedown', handler);
        document.addEventListener('keydown', escHandler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('keydown', escHandler);
        };
    }, [open]);

    const visibleItems = items.filter(Boolean);
    if (!visibleItems.length) return null;

    return (
        <div ref={ref} className="relative">
            <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01" />
                </svg>
            </Button>
            {open && (
                <div className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20">
                    {visibleItems.map((item, i) => (
                        <button
                            key={i}
                            onClick={() => { setOpen(false); item.onClick(); }}
                            className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'}`}
                        >
                            {item.icon && <span className="text-gray-400 shrink-0">{item.icon}</span>}
                            <span className="truncate">{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status }) {
    const value = status || 'new';
    const tone = STATUS_TONE[value] || STATUS_TONE.new;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${tone}`}>
            {value}
        </span>
    );
}

function formatRelative(iso) {
    if (!iso) return '—';
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'vừa xong';
    if (min < 60) return `${min} phút trước`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} giờ trước`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} ngày trước`;
    return new Date(iso).toLocaleDateString('vi-VN');
}

export default function AdminInsightDetail() {
    const { insightId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [insight, setInsight] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const tabParam = searchParams.get('tab');
    const activeTab = TAB_NAMES.includes(tabParam) ? tabParam : 'content';
    const setActiveTab = (next) => {
        const params = new URLSearchParams(searchParams);
        if (next === 'content') params.delete('tab');
        else params.set('tab', next);
        setSearchParams(params, { replace: true });
    };
    const [transcriptModal, setTranscriptModal] = useState(null);
    const [transcriptLoading, setTranscriptLoading] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [stats, setStats] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [statusDraft, setStatusDraft] = useState('new');
    const [statusNote, setStatusNote] = useState('');
    const [statusUpdating, setStatusUpdating] = useState(false);

    useEffect(() => { load(); }, [insightId]);

    useEffect(() => {
        if (!insight?.id) return;
        let cancelled = false;
        adminApi.getInstrumentInsightStats(insight.id, STATS_QUERY)
            .then((res) => { if (!cancelled) setStats(res); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [insight?.id]);

    const load = async () => {
        try {
            setLoading(true);
            const res = await adminApi.getInstrumentInsightDetail(insightId);
            setInsight(res);
            setError(null);
        } catch (err) {
            setError(err.message || 'Không tải được bài viết');
        } finally {
            setLoading(false);
        }
    };

    const handleReindex = async () => {
        try {
            await adminApi.reindexInstrumentInsight(insightId);
            setSuccessMessage('Đã thêm vào hàng đợi reindex — refresh trang sau vài giây.');
            setTimeout(() => setSuccessMessage(null), FLASH_TIMEOUT_MS);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleViewTranscript = async () => {
        setTranscriptModal({ markdown: null, presignedUrl: null, s3Key: null });
        setTranscriptLoading(true);
        try {
            const data = await adminApi.getInstrumentInsightTranscript(insightId, { inline: true });
            setTranscriptModal({
                markdown: data.markdown,
                presignedUrl: data.presigned_url,
                s3Key: data.s3_key,
            });
        } catch (err) {
            setTranscriptModal({
                markdown: null,
                presignedUrl: null,
                s3Key: null,
                error: err.message || 'Không tải được transcript',
            });
        } finally {
            setTranscriptLoading(false);
        }
    };

    const handleOpenPublicDetail = () => {
        window.open(`/insights/${encodeURIComponent(insight.document_id)}`, '_blank', 'noopener');
    };

    const handleDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            await adminApi.deleteInstrumentInsight(insight.id);
            navigate('/admin/instrument-intelligence', {
                state: { flash: `Đã xóa bài viết ${insight.document_id}` },
            });
        } catch (err) {
            setError(err.message || 'Xóa thất bại');
            setDeleting(false);
            setDeleteOpen(false);
        }
    };

    const openStatusModal = () => {
        setStatusDraft(insight.status || 'new');
        setStatusNote(insight.review_note || '');
        setStatusModalOpen(true);
    };

    const closeStatusModal = () => {
        if (statusUpdating) return;
        setStatusModalOpen(false);
    };

    const handleStatusUpdate = async () => {
        setStatusUpdating(true);
        setError(null);
        try {
            const res = await adminApi.updateInstrumentInsightStatus(insight.id, {
                status: statusDraft,
                reviewNote: statusNote.trim() || null,
            });
            setInsight((prev) => ({
                ...prev,
                status: res.status,
                reviewed_by: res.reviewed_by,
                reviewed_at: res.reviewed_at,
                review_note: res.review_note,
            }));
            setSuccessMessage(`Đã đổi trạng thái sang "${res.status}"`);
            setTimeout(() => setSuccessMessage(null), FLASH_TIMEOUT_MS);
            setStatusModalOpen(false);
        } catch (err) {
            setError(err.message || 'Cập nhật trạng thái thất bại');
        } finally {
            setStatusUpdating(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Đang tải bài viết..." center />;
    if (!insight) return <Alert variant="error">Không tìm thấy bài viết</Alert>;

    const sinks = insight.sinks || {};
    const sinkEntries = Object.entries(sinks);

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-6">
                <div>
                    <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => {
                            if (window.history.state?.idx > 0) navigate(-1);
                            else navigate('/admin/instrument-intelligence');
                        }}
                    >
                        ← Quay lại
                    </Button>
                    <h1 className="text-xl font-semibold text-gray-900 mt-1 break-words">
                        {insight.structured?.title || insight.summary_text || insight.title || (
                            <span className="text-gray-400 italic">(no title)</span>
                        )}
                    </h1>
                    <p className="text-sm font-mono text-gray-500 mt-1 break-all">
                        {insight.document_id}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                        {CATEGORY_LABEL[insight.category] || insight.category} · v{insight.version} ·
                        {' '}{new Date(insight.snapshot_at).toLocaleString()}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    <ActionMenu
                        items={[
                            { label: 'Đổi trạng thái', icon: ICONS.status, onClick: openStatusModal },
                            { label: 'Mở trang chi tiết', icon: ICONS.external, onClick: handleOpenPublicDetail },
                            insight.transcript_s3_key && { label: 'Xem transcript', icon: ICONS.transcript, onClick: handleViewTranscript },
                            { label: 'Refresh', icon: ICONS.refresh, onClick: load },
                            { label: 'Reindex', icon: ICONS.reindex, onClick: handleReindex },
                            { label: 'Xóa', icon: ICONS.trash, onClick: () => setDeleteOpen(true), danger: true },
                        ]}
                    />
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            {(insight.structured?.summary || insight.summary_text) && (
                <div className="border-l-4 border-indigo-200 bg-indigo-50/40 px-4 py-3 mb-4 rounded-r-md">
                    <div className="text-[11px] text-indigo-700 uppercase tracking-wide mb-1">Tóm tắt</div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                        {insight.structured?.summary || insight.summary_text}
                    </p>
                </div>
            )}

            <div className="grid grid-cols-4 gap-3 mb-6">
                <div className="border border-gray-200 rounded-lg bg-white p-3">
                    <div className="text-[11px] text-gray-500 uppercase">Tổng lượt xem</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
                        {Number(stats?.totals?.view_count_total ?? insight.view_count ?? 0).toLocaleString('vi-VN')}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                        Cập nhật {formatRelative(stats?.totals?.updated_at)}
                    </div>
                </div>
                <div className="border border-gray-200 rounded-lg bg-white p-3">
                    <div className="text-[11px] text-gray-500 uppercase">Người xem (unique)</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
                        {Number(stats?.totals?.view_count_unique ?? 0).toLocaleString('vi-VN')}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">HyperLogLog ±0.81%</div>
                </div>
                <div className="border border-gray-200 rounded-lg bg-white p-3">
                    <div className="text-[11px] text-gray-500 uppercase">Status</div>
                    <div className="mt-1">
                        <StatusBadge status={insight.status} />
                    </div>
                    {insight.reviewed_at && (
                        <div className="text-[11px] text-gray-500 mt-2 leading-snug">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <span>{new Date(insight.reviewed_at).toLocaleString('vi-VN')}</span>
                                {insight.reviewed_by && <span>· bởi {insight.reviewed_by}</span>}
                            </div>
                            {insight.review_note && (
                                <div className="text-gray-700 italic mt-1 break-words">"{insight.review_note}"</div>
                            )}
                        </div>
                    )}
                </div>
                <div className="border border-gray-200 rounded-lg bg-white p-3">
                    <div className="text-[11px] text-gray-500 uppercase">Source</div>
                    <div className="text-sm font-medium text-gray-800 mt-1">
                        {typeof insight.source === 'string' && insight.source
                            ? insight.source
                            : insight.source_meta?.kind
                            ?? (typeof insight.source === 'object' ? insight.source?.kind : null)
                            ?? '—'}
                    </div>
                    {insight.severity && (
                        <div className="text-[11px] text-gray-500 mt-2">
                            severity: <span className="font-medium text-gray-700">{insight.severity}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="border-b border-gray-200 mb-3">
                <nav className="flex gap-4">
                    {TAB_NAMES.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {tab === 'sinks' ? `Sinks (${sinkEntries.length})` : TAB_LABELS[tab]}
                        </button>
                    ))}
                </nav>
            </div>

            <div className={activeTab === 'stats' ? '' : 'border border-gray-200 rounded-lg bg-white p-5'}>
                {activeTab === 'stats' && <InsightStatsPanel insightId={insight.id} />}
                {activeTab === 'content' && (
                    <div className="max-w-[750px] mx-auto">
                        <RenderedContent
                            contentType={insight.content_type}
                            content={insight.content}
                            structured={insight.structured}
                        />
                    </div>
                )}
                {activeTab === 'structured' && (
                    <pre className="text-xs font-mono text-gray-700 overflow-x-auto">
                        {insight.structured ? JSON.stringify(insight.structured, null, 2) : '(none)'}
                    </pre>
                )}
                {activeTab === 'attributes' && (
                    <pre className="text-xs font-mono text-gray-700 overflow-x-auto">
                        {JSON.stringify(insight.attributes || {}, null, 2)}
                    </pre>
                )}
                {activeTab === 'source_meta' && (
                    <pre className="text-xs font-mono text-gray-700 overflow-x-auto">
                        {JSON.stringify(
                            insight.source_meta
                                ?? (typeof insight.source === 'object' ? insight.source : {}),
                            null,
                            2,
                        )}
                    </pre>
                )}
                {activeTab === 'sinks' && (
                    <div className="space-y-3">
                        <p className="text-xs text-gray-500">
                            Nơi bài viết đã được phân phối. S3 lưu payload JSON gốc, RAG index để agent truy
                            vấn, DB là bản ghi chính thức.
                        </p>
                        {sinkEntries.length === 0 ? (
                            <Alert variant="warning">
                                Chưa có sink nào commit. Bấm <b>Reindex</b> để enqueue phân phối tới S3 + RAG.
                            </Alert>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {sinkEntries.map(([name, sink]) => (
                                    <SinkCard key={name} sink={{ ...sink, sink_name: sink.sink_name || name }} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Modal
                open={!!transcriptModal}
                title="Transcript phiên"
                onClose={() => setTranscriptModal(null)}
                size="xl"
            >
                {transcriptModal && (
                    <div className="p-5 space-y-3">
                        {transcriptLoading ? (
                            <Spinner size="md" text="Đang tải transcript..." center />
                        ) : transcriptModal.error ? (
                            <Alert variant="warning">{transcriptModal.error}</Alert>
                        ) : (
                            <>
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                    <code className="font-mono">{transcriptModal.s3Key}</code>
                                    {transcriptModal.presignedUrl && (
                                        <a
                                            href={transcriptModal.presignedUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-600 hover:text-blue-700"
                                        >
                                            Tải file gốc ↗
                                        </a>
                                    )}
                                </div>
                                <div className="border border-gray-200 rounded-lg bg-white p-4 max-h-[70vh] overflow-y-auto">
                                    <RichTextRenderer text={transcriptModal.markdown || '_(empty)_'} />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                open={deleteOpen}
                onClose={() => !deleting && setDeleteOpen(false)}
                title="Xóa vĩnh viễn bài viết này?"
                size="sm"
            >
                <Modal.Body>
                    <p className="text-sm text-gray-700">
                        Hành động này <span className="font-semibold text-red-600">không thể hoàn tác</span>.
                        Toàn bộ lượt xem, bucket thống kê và pointer phiên bản sẽ bị xóa cùng.
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs font-mono text-gray-700 break-all">
                        {insight.document_id}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                        Huỷ
                    </Button>
                    <Button variant="danger" size="sm" onClick={handleDelete} loading={deleting}>
                        Xóa vĩnh viễn
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                open={statusModalOpen}
                onClose={closeStatusModal}
                title="Đổi trạng thái bài viết"
                size="sm"
            >
                <Modal.Body>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Trạng thái</label>
                        <select
                            value={statusDraft}
                            onChange={(e) => setStatusDraft(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 bg-white"
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-gray-500 mt-1">
                            Chỉ <span className="font-medium">published</span> mới hiển thị trên feed cho người dùng.
                        </p>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Ghi chú <span className="text-gray-400">(tùy chọn)</span>
                        </label>
                        <textarea
                            value={statusNote}
                            onChange={(e) => setStatusNote(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            placeholder="Ghi chú nội bộ — lý do, context..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={closeStatusModal} disabled={statusUpdating}>
                        Huỷ
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleStatusUpdate} loading={statusUpdating}>
                        Cập nhật
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
